import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useGlobalState } from '@/state'
import { formatInTimezone } from '@/utils/date'
import { ArrowLeftIcon, CalendarIcon, CashIcon, ExclamationIcon, VolumeUpIcon } from '@heroicons/react/solid'

import Loading from '@/shared/Loading'
import useQuery from '@/hooks/useQuery'

const CARD = 'bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xl shadow-slate-900/[0.07] p-6 sm:p-8'

const JobShow = () => {
  const { id } = useParams()
  const [currentUser] = useGlobalState('currentUser')
  const { getRequest } = useQuery()
  const [ job, setJob ] = useState(null)
  const [ canViewDetails, setCanViewDetails ] = useState(false)
  const [ loading, setLoading ] = useState(true)
  const [ notFound, setNotFound ] = useState(false)

  useEffect(() => {
    setLoading(true)
    getRequest(`/api/v1/jobs/${id}`, {}, (err, jsonData) => {
      setLoading(false)
      if (err) { setNotFound(true); return }
      setJob(jsonData.job)
      setCanViewDetails(jsonData.canViewDetails)
    })
  }, [id])

  if (loading) { return <Loading /> }

  if (notFound || !job) {
    return <div className='py-8'>
      <div className={CARD}>
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100">Job not found</h1>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          This job may have been taken down since you last saw it.
        </p>
        <Link to='/dashboard' className="mt-5 inline-flex items-center text-sm font-bold text-clcblue dark:text-clcnavy-light hover:text-clcnavy-dark transition-colors">
          <ArrowLeftIcon className="h-4 w-4 mr-1" aria-hidden="true" />
          Back to dashboard
        </Link>
      </div>
    </div>
  }

  return <div className='py-8'>
    <Link to='/dashboard' className="inline-flex items-center text-sm font-bold text-clcblue dark:text-clcnavy-light hover:text-clcnavy-dark transition-colors">
      <ArrowLeftIcon className="h-4 w-4 mr-1" aria-hidden="true" />
      Back to dashboard
    </Link>

    <div className={`mt-4 ${CARD}`}>
      { job.expired && <div className="mb-4 inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider text-slate-600 bg-slate-100 dark:text-slate-300 dark:bg-slate-800">
        Deadline passed
      </div> }

      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100 leading-tight">
        {job.name}
      </h1>

      <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-500 dark:text-slate-400">
        { job.rate && <span className="inline-flex items-center">
          <CashIcon className='w-4 h-4 mr-1.5 shrink-0 text-slate-400 dark:text-slate-500' />
          <span className="font-bold text-slate-700 dark:text-slate-300">{job.rate}</span>
        </span> }
        { job.volume && <span className="inline-flex items-center">
          <VolumeUpIcon className='w-4 h-4 mr-1.5 shrink-0 text-slate-400 dark:text-slate-500' />
          {job.volume}
        </span> }
        <span className="inline-flex items-center">
          <CalendarIcon className='w-4 h-4 mr-1.5 shrink-0 text-slate-400 dark:text-slate-500' />
          {job.expired ? 'Closed' : 'Due'} {formatInTimezone(job.deadlineUtc, currentUser.timezone, 'EEE MMM d, Y h:mmaaa zzz')}
        </span>
      </div>

      { canViewDetails ? <>
        {/* The description carries the poster's own apply instructions, a link, an
            email address, a request for headshots. There is no apply button by design. */}
        { job.description && <div
          className='mt-6 pt-6 border-t border-slate-200 dark:border-slate-800 prose dark:prose-invert max-w-none text-slate-700 dark:text-slate-300'
          dangerouslySetInnerHTML={{ __html: job.description }}
        /> }
      </> : (
        <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-800">
          <div className="rounded-md bg-yellow-50 dark:bg-yellow-950/30 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <ExclamationIcon className="h-5 w-5 text-yellow-400 dark:text-yellow-300" aria-hidden="true" />
              </div>
              <div className="ml-3">
                <div className='pb-3 text-sm text-yellow-800 dark:text-yellow-200'>
                  Full job details are only available to members.
                </div>
                <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                  To read the full brief and apply to this job, please <Link to='/subscriptions'>upgrade to the membership</Link>.
                </h3>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  </div>
}

export default JobShow
